import React, { Suspense } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { Container, Fab, Tab, Tabs, } from '@mui/material';
import { Add } from '@mui/icons-material';
import { a11yProps, TabPanels } from 'components/mui/Tabs';
import useSwiperTabs from 'hooks/useSwiperTabs';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import { VoyageStatus } from 'models/VoyageModel';
import { CompanyType } from 'models/CompanyModel';
import useVoyageModals from 'modules/voyages/hooks/useVoyageModals';
import VoyagesDesktop from 'modules/voyages/views/VoyagesDesktop';
import VoyagesMobile from 'modules/voyages/views/VoyagesMobile';
import auth from 'recoil/atoms/auth';
import voyageCount from 'recoil/atoms/voyageCount';


const allStatuses = Object.values(VoyageStatus);
const partyStatuses = [
    VoyageStatus.Pending,
    VoyageStatus.Loaded,
    VoyageStatus.Departed,
    VoyageStatus.Arrived,
];

const VoyagesInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const statuses = my.type === CompanyType.Party ? partyStatuses : allStatuses;
    const [status, setStatus] = useQueryParam('status', withDefault(StringParam, statuses[0]));
    const {index, setIndex, setSwiper} = useSwiperTabs(statuses.indexOf(status as VoyageStatus));
    const counts = useRecoilValue(voyageCount);
    const {openAddVoyageModal} = useVoyageModals();

    const change = (idx: number, fromSwiper?: boolean) => {
        setIndex(idx, fromSwiper);
        setStatus(statuses[idx]);
    };

    useDidUpdateEffect(() => setIndex(statuses.indexOf(status as VoyageStatus)), [status]);

    const id = 'voyages';

    const View = isMobile ? VoyagesMobile : VoyagesDesktop;

    return (
        <Container maxWidth={false} sx={{mt: 2}}>
            <Tabs
                variant="scrollable"
                value={index}
                onChange={(e, v) => change(v)}
                aria-label="Voyage status tabs"
            >
                {statuses.map(s => <Tab key={s} label={`${t('status_' + s)} (${counts[s] || 0})`} {...a11yProps(id, s)}/>)}
            </Tabs>

            <TabPanels
                active={index}
                id={id}
                setIndex={change}
                setSwiper={setSwiper}
                narrow
                tabs={statuses.map(s => <View key={s} status={s}/>)}
            />

            {isMobile && my.permissions.has('voyages create') &&
                <Fab
                    color="primary"
                    aria-label="add car"
                    sx={{
                        position: "fixed",
                        bottom: theme => theme.spacing(2),
                        right: theme => theme.spacing(2)
                    }}
                    onClick={openAddVoyageModal}
                >
                    <Add/>
                </Fab>
            }
        </Container>
    )
};

const Voyages = () => {
    return (
        <Suspense fallback={null}>
            <VoyagesInner/>
        </Suspense>
    )
};

export default Voyages;
